import { t } from '@/lib/i18n'
import type { GlyphName } from './glyphs'

export interface NavItem {
  to: string
  /** Translated label. */
  label: () => string
  /** The r5 glyph: rest when inactive, active (filled) when current. */
  glyph: GlyphName
  /** Extra paths that keep this item active (the old names stay reachable: `/dashboard`, `/my-exams`). */
  matchPaths?: readonly string[]
  /** Active only on exactly this path (the school Overview is `/school`, and every other school path starts with it). */
  exact?: boolean
}

/** Speaking (`/keep-talking`) is only a destination when the build flag is exactly 'true' (the route 404s otherwise). */
export function keepTalkingEnabled(): boolean {
  return import.meta.env.VITE_ENABLE_KEEP_TALKING === 'true'
}

/**
 * The five primary destinations, plain names (DECISIONS 10): Home, Practice, Speaking, Results, Plan. With the Keep Talking
 * flag off the bar has four items. Account items (Profile, Settings, Achievements, Leaderboard, Sign out) live in the avatar menu.
 */
export function primaryNavItems(): NavItem[] {
  const items: NavItem[] = [
    { to: '/home', label: () => t('Home'), glyph: 'home', matchPaths: ['/dashboard'] },
    { to: '/exams-library', label: () => t('Practice'), glyph: 'practice' },
  ]
  if (keepTalkingEnabled()) items.push({ to: '/keep-talking', label: () => t('Speaking'), glyph: 'talk' })
  items.push(
    { to: '/results', label: () => t('Results'), glyph: 'results', matchPaths: ['/my-exams'] },
    { to: '/plan', label: () => t('Plan'), glyph: 'plan' },
  )
  return items
}

const within = (pathname: string, base: string) => pathname === base || pathname.startsWith(`${base}/`)

/** Active when the pathname is the item's route or below it, or one of its `matchPaths`. */
export function isNavActive(item: NavItem, pathname: string): boolean {
  if (item.exact) return pathname === item.to
  return within(pathname, item.to) || (item.matchPaths ?? []).some((path) => within(pathname, path))
}

/** Index of the active item, or -1 (Profile, Settings and the other avatar pages highlight nothing). */
export function activeNavIndex(items: readonly NavItem[], pathname: string): number {
  return items.findIndex((item) => isNavActive(item, pathname))
}
